import { Box, Inline, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';
import { messages } from '@databases/intl/messages';
import { useHoverState } from '@databases/react';
import React, { type FunctionComponent, useMemo } from 'react';
import { useIntl } from 'react-intl-next';

import AddCommentIcon from './AddCommentIcon';
import CommentIcon from './CommentIcon';

const ViewThreadButtonStyles = xcss({
  ':hover': {
    cursor: 'pointer',
  },
  borderRadius: 'radius.small',
  paddingInline: 'space.025',
});

const ViewThreadButtonNotSelectedStyles = xcss({
  ':hover': {
    backgroundColor: 'color.background.neutral.hovered',
  },
});

const ViewThreadButtonSelectedStyles = xcss({
  ':hover': {
    backgroundColor: 'color.background.accent.blue.subtlest',
  },
});

const AddThreadButtonStyles = xcss({
  borderRadius: 'radius.small',
  ':hover': {
    cursor: 'pointer',
  },
});

export interface CellCommentsButtonProps {
  onClick: (e: React.MouseEvent) => void;
  commentToggled: boolean;
  hasThreads: boolean;
  commentCount: number;
  shouldShowHoverButton: boolean;
  valueId: string;
}

const CellCommentsButton: FunctionComponent<CellCommentsButtonProps> = ({
  onClick,
  commentToggled,
  hasThreads,
  commentCount,
  shouldShowHoverButton,
  valueId,
}) => {
  const { formatMessage } = useIntl();
  const [isHovering, hoverProps] = useHoverState();

  const commentString = useMemo(() => {
    return commentCount > 9 ? '9+' : commentCount.toString();
  }, [commentCount]);

  const additionalThreadButtonStyles = useMemo(() => {
    return commentToggled
      ? ViewThreadButtonSelectedStyles
      : ViewThreadButtonNotSelectedStyles;
  }, [commentToggled]);

  if (hasThreads) {
    return (
      <Tooltip
        position="top"
        content={formatMessage(messages['comments.show-comment-thread-button-tooltip'], {
          isMulti: commentCount,
        })}
      >
        <Box
          onClick={onClick}
          xcss={[ViewThreadButtonStyles, additionalThreadButtonStyles]}
          id={`cell-view-thread-button-${valueId}`}
        >
          <Inline
            space="space.075"
            alignBlock="center"
            xcss={xcss({
              color: commentToggled ? 'color.text.accent.blue' : 'color.text.subtle',
            })}
          >
            <CommentIcon
              label={formatMessage(messages['comments.view-comment-thread-button-label'])}
              color={
                commentToggled
                  ? token('color.text.accent.blue', '#0055CC')
                  : token('color.text.subtle', '#44546F')
              }
            />
            {commentString}
          </Inline>
        </Box>
      </Tooltip>
    );
  }

  if (shouldShowHoverButton) {
    return (
      <Tooltip
        position="top"
        content={formatMessage(messages['comments.add-comment-thread-button-label'])}
      >
        <Box
          xcss={AddThreadButtonStyles}
          onClick={onClick}
          {...hoverProps}
          id={`cell-add-thread-button-${valueId}`}
        >
          <AddCommentIcon
            label={formatMessage(messages['comments.add-comment-thread-button-label'])}
            color={
              isHovering
                ? token('color.text.subtle', '#44546F')
                : token('color.text.subtlest', '#626F86')
            }
          />
        </Box>
      </Tooltip>
    );
  }

  return null;
};

export default CellCommentsButton;
